import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, Divider, Flex, Form, Input, Select, Skeleton, Space, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import useApiClient from '../../hooks/api/useApiClient';
import { useCreateTaxCategoryMutation } from '../../store/api-slices';
import { TAX_SCOPE } from '../../services/api/tax-category/TaxCategoriesManager';
import PercentageInput from '../form/general/PercentageInput';
import {
  BLANK_TAX_CATEGORY_DRAFT,
  TAX_CATEGORIES_STATUS,
  isDraftSaveable,
  taxCategoryRateSummary,
  toRequestPayload,
} from './taxCategory.utils';

/**
 * Shared tax-category field for ProductModal and CategoryModal: pick an
 * existing category or create one on the spot. There is no manual rate - the
 * per-row tax_percentage is retired, so every rate lives on a tax category.
 * `scope` decides who owns a category created here — the selected restaurant
 * on a normal menu, the franchise's service provider on a master menu, where
 * one row is shared by every outlet.
 * `appliesDefaultWhenEmpty` marks a new row the backend will link to the
 * menu's default category when nothing is picked, so the hint says so rather
 * than promising the standard rate.
 * `value` is always the raw tax_category_id — never a resolved object — so a
 * category the caller's list hasn't loaded yet round-trips unchanged instead
 * of silently reading as unlinked.
 */
export default function TaxCategoryPicker({
  taxCategories,
  taxCategoriesStatus,
  value,
  onChange,
  onCreated,
  allowCreate = true,
  scope = TAX_SCOPE.COMPANY,
  appliesDefaultWhenEmpty = false,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Tax:TaxCategoryPicker' });
  // The region-wide / partner-wide / restaurant-wide labels are shared with the
  // tax screen rather than duplicated per catalog.
  const { t: tScope } = useTranslation(undefined, { keyPrefix: 'Components:Tax:TaxCurrencySettings' });
  const { TaxCategoriesManager } = useApiClient();
  const [createTaxCategory] = useCreateTaxCategoryMutation();

  const [draft, setDraft] = useState(BLANK_TAX_CATEGORY_DRAFT);
  const [creating, setCreating] = useState(false);
  // Categories created from this picker, kept until the caller's list catches
  // up. onCreated() refetches that list, and a refetch can fail - without this
  // the freshly created (and already selected) category would not be in
  // `taxCategories`, so the Select would fall back to rendering its raw id.
  const [createdCategories, setCreatedCategories] = useState([]);
  const nameInputRef = useRef(null);

  // The caller's copy wins on id collision: once the refetch lands it carries
  // the server's canonical row, and this local one stops mattering.
  const knownCategories = useMemo(() => {
    if (createdCategories.length === 0) {
      return taxCategories;
    }

    const listed = new Set(taxCategories.map((category) => category.id));

    return [...taxCategories, ...createdCategories.filter((category) => !listed.has(category.id))];
  }, [taxCategories, createdCategories]);

  const options = useMemo(
    () =>
      knownCategories.map((category) => {
        const { defaultRate, hasChannelOverrides } = taxCategoryRateSummary(category);

        return {
          value: category.id,
          label: category.name,
          scope: category.scope ?? null,
          rateLabel:
            defaultRate === null
              ? null
              : `${defaultRate}%${hasChannelOverrides ? ` ${t('option.varies_by_channel')}` : ''}`,
        };
      }),
    [knownCategories, t],
  );

  async function addCategory(e) {
    e.preventDefault();

    if (!isDraftSaveable(draft) || creating) {
      return;
    }

    setCreating(true);
    const result = await createTaxCategory({
      apiManager: TaxCategoriesManager,
      scope,
      ...toRequestPayload(draft),
    });
    setCreating(false);

    if (result.error) {
      return toast.error(t('toasts.create_error'));
    }

    setDraft(BLANK_TAX_CATEGORY_DRAFT);

    if (result.data?.id) {
      setCreatedCategories((current) => [...current, result.data]);
    }

    onChange(result.data?.id ?? null);
    await onCreated?.();
    setTimeout(() => nameInputRef.current?.focus(), 0);
  }

  if (taxCategoriesStatus === TAX_CATEGORIES_STATUS.LOADING) {
    return (
      <Form.Item label={t('field.label')}>
        <Skeleton.Input active block />
      </Form.Item>
    );
  }

  // The list failed to load: no Select at all, linked or not. If value is
  // already set, freezing it here (no way to touch it) is what keeps
  // buildTaxCategoryFields's canUnlink=false guarantee meaningful - there's
  // nothing in this render that could send an unlink.
  if (taxCategoriesStatus === TAX_CATEGORIES_STATUS.ERROR) {
    return (
      <Form.Item label={t('field.label')}>
        <Typography.Text type="warning">{t('load_error')}</Typography.Text>
      </Form.Item>
    );
  }

  const isEmpty = knownCategories.length === 0;
  // The create form below is gated on allowCreate, so the "add one below" copy
  // would point a read-only user at something that is not rendered for them.
  const emptyHint = allowCreate ? t('empty.hint') : t('empty.read_only_hint');

  return (
    <Form.Item
      label={t('field.label')}
      htmlFor="tax_category_id"
      extra={isEmpty ? emptyHint : !value && t(appliesDefaultWhenEmpty ? 'field.default_hint' : 'field.unlinked_hint')}
    >
      <Select
        id="tax_category_id"
        allowClear
        showSearch={{ optionFilterProp: 'label' }}
        value={value ?? undefined}
        placeholder={t('field.placeholder')}
        onChange={(v) => onChange(v ?? null)}
        options={options}
        notFoundContent={isEmpty ? emptyHint : undefined}
        labelRender={({ label, value: selectedId }) => {
          const rateLabel = options.find((option) => option.value === selectedId)?.rateLabel;

          return (
            <>
              {label}
              {rateLabel && <Typography.Text type="secondary"> · {rateLabel}</Typography.Text>}
            </>
          );
        }}
        optionRender={(option) => (
          <Flex justify="space-between" align="center" className="gap-2">
            <Flex align="center" className="gap-2">
              <span>{option.data.label}</span>
              {option.data.scope && <Tag>{tScope(`scope_labels.${option.data.scope}`)}</Tag>}
            </Flex>
            {option.data.rateLabel && <Typography.Text type="secondary">{option.data.rateLabel}</Typography.Text>}
          </Flex>
        )}
        popupRender={(menu) => (
          <>
            {menu}
            {allowCreate && (
              <>
                <Divider style={{ margin: '8px 0' }} />
                <Space orientation="vertical" style={{ padding: '0 8px 8px', width: '100%' }}>
                  <Space.Compact style={{ width: '100%' }}>
                    <Input
                      ref={nameInputRef}
                      placeholder={t('create.name_placeholder')}
                      value={draft.name}
                      onChange={(e) => setDraft((current) => ({ ...current, name: e.target.value }))}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                    <PercentageInput
                      placeholder={t('create.rate_placeholder')}
                      value={draft.default_tax_rate}
                      onChange={(rate) => setDraft((current) => ({ ...current, default_tax_rate: rate }))}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </Space.Compact>
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={addCategory}
                    loading={creating}
                    disabled={!isDraftSaveable(draft)}
                    block
                  >
                    {t('create.button')}
                  </Button>
                </Space>
              </>
            )}
          </>
        )}
      />
    </Form.Item>
  );
}
